import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, Inject, PLATFORM_ID } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoaderService } from '../../core/services/loader.service';
import { FlashMessageService } from '../../shared/components/flash-message/flash-message.service';
import { ActivatedRoute, NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { AuthenticationService } from '../../core/services/authentication.service';
import { ProfileService } from './profile.service';
import { CompanyPlansComponent } from "../../shared/components/company-plans/company-plans.component";
import { MySubscriptionComponent } from './my-subscription/my-subscription.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureStatusModel, FeatureStatusResponseModel } from '../../models/company.model';
import { BootstrapModalService } from '../../core/services/bootstrap-modal.service';
import { AddUpdatePostComponent } from "../../shared/components/add-update-post/add-update-post.component";
import { MarketingActionsComponent } from "../../shared/components/marketing-actions/marketing-actions.component";
import { filter } from 'rxjs';

@Component({
  selector: 'app-profile',
  imports: [CommonModule, TranslateModule, RouterOutlet, RouterModule, CompanyPlansComponent, AddUpdatePostComponent, MarketingActionsComponent],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent {

  private readonly destroyRef = inject(DestroyRef);
  public isBrowser: boolean;
  public selectedLang: string;
  currentUser: any;
  companyName: any;
  plansData: any = null;
  resetDays: number = 0;
  featureStatus!: FeatureStatusResponseModel;
  featureMap: Record<string, FeatureStatusModel> = {};
  modalData = { title: '', message1: '', message2: '' };
  isPost: boolean = false;
  hideProfileSidebar = false;

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object,
    @Inject(DOCUMENT) private readonly document: Document,
    public loaderService: LoaderService,
    public translate: TranslateService,
    public authenticationService: AuthenticationService,
    private readonly profileService: ProfileService,
    public readonly router: Router,
    private readonly bootstrapModalService: BootstrapModalService,
    private readonly cdr: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private readonly flashMessage: FlashMessageService) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.selectedLang = this.translate.getCurrentLang();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: any) => {
      if (this.selectedLang !== event.lang) {
        this.selectedLang = event.lang;
      }
    });
    this.authenticationService.currentUser.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.currentUser = user;
      if (user?.RoleId) {
        this.getCompanyName();
        this.getFeatureStatus();
      }
    });
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      this.updateLayout();
    });
  }

  getFeatureStatus() {
    if (this.currentUser?.RoleId === 1) {
      this.profileService.getFeatureStatus().subscribe(res => {
        res.forEach((f: any) => {
          this.featureMap[f.FeatureKey] = new FeatureStatusModel(f);
        });
        this.featureStatus = new FeatureStatusResponseModel({
          Features: this.featureMap
        });
        if (this.isBrowser) {
          const data = localStorage.getItem('currentUser');
          if (data) {
            const currentUser = JSON.parse(data);
            currentUser.PlanId = res[0].PlanId;
            localStorage.setItem('currentUser', JSON.stringify(currentUser));
          }
        }
      });
    }
  }

  private updateLayout(): void {
    let currentRoute = this.route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    this.hideProfileSidebar = currentRoute.snapshot.data['hideProfileSidebar'] ?? false;
  }

  onChildActivate(component: any) {
    this.plansData = null;

    if (component instanceof MySubscriptionComponent) {
      component.plansLoaded.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
        this.plansData = data;
      });
    }

    if (component?.scrollToPlans) {
      component.scrollToPlans.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.scrollToTable();
      });
    }
  }

  scrollToTable() {
    if (!this.isBrowser) return;
    setTimeout(() => {
      const el = this.document.getElementById('plansSection');
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.pageYOffset - 145;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }, 50);
  }

  getUserName(user: any): string {
    let name = '';
    if (!user) return name;
    if (this.currentUser?.RoleId === 1 || this.currentUser?.RoleId === 4 || this.currentUser?.RoleId === 6) {
      name = this.getInitials(user.CompanyName);
    } else if (user.FirstName && user.LastName) {
      name = user.FirstName.charAt(0).toUpperCase() + user.LastName.charAt(0).toUpperCase();
    } else if (user.FirstName) {
      name = user.FirstName.charAt(0).toUpperCase();
    }

    return name;
  }

  getInitials(text: string, limit = 2): string {
    return text?.trim().split(' ').filter(Boolean).map(w => w[0].toUpperCase()).slice(0, limit).join('') || '';
  }

  formatName(text: string): string {
    if (!text) return '';

    const parts = text.trim().split(' ');
    return parts[0] + (parts[1] ? ' ' + parts[1][0] : '');
  }

  getCompanyName() {
    this.loaderService.display(true);
    this.profileService.getSkeinCompanyName().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.companyName = data.companyName;
        this.loaderService.display(false);
      },
      error: () => {
        this.flashMessage.showErrorAlert(this.translate.instant("error-message"));
        this.loaderService.display(false);
      }
    });
  }

  gotoCompanyPage() {
    void this.router.navigate(['/bim/company', this.companyName], {
      state: { fromSkein: true }
    });
  }

  handlePublish(featureKey: string, route: string) {
    this.isPost = false;
    this.cdr.detectChanges();
    const feature = this.featureStatus?.Features[featureKey];

    if (!feature || feature.IsEnabled === false) {
      this.flashMessage.showWarningAlert(this.translate.instant('feature_not_available'));
      return;
    }

    this.bootstrapModalService.close('publishContentMenu');
    if (feature.IsExceeded) {
      if (feature.ResetsInDays) {
        const key = feature.ResetsInDays === 1 ? 'try_again_day' : 'try_again_days';
        this.modalData = {
          title: this.translate.instant('limit_reached'),
          message1: this.translate.instant('limit_reached_message'),

          message2: this.translate.instant(key, { days: feature.ResetsInDays })
        };
      }
      else {
        this.modalData = {
          title: this.translate.instant('limit_reached'),
          message1: this.translate.instant('limit_reached_message'),
          message2: this.translate.instant('limit_reached_upgrade')
        };

      }
      this.cdr.detectChanges();
      this.bootstrapModalService.open('profileLimitInfoModal');
      return;
    }
    if (featureKey === 'post') {
      this.isPost = true;
      this.cdr.detectChanges();
      this.bootstrapModalService.open('publishPostModal', {
        backdrop: 'static',
        keyboard: false
      });
    } else {
      void this.router.navigate([route]);
    }
  }

  isLocked(key: string): boolean {
    return this.featureStatus?.Features?.[key]?.IsEnabled !== true;
  }

  hidePostModal() {
    this.bootstrapModalService.close('publishPostModal');
    this.isPost = false;
    this.cdr.detectChanges();
  }

  onPublishedPost() {
    this.getFeatureStatus();
    this.bootstrapModalService.close('publishPostModal');
    this.cdr.detectChanges();
    this.bootstrapModalService.open('PublishSuccessModal');
  }

  goToHome() {
    this.bootstrapModalService.close('PublishSuccessModal');
    this.cdr.detectChanges();
    void this.router.navigate(['/home']);
  }
}